"use client";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import logoSvg from "@/asset/icons/klarda-media-logo.svg";
import background from "@/asset/images/third-bg.png";
import { motion, useInView, useAnimation } from "framer-motion";

const summary = [
  {
    label: "Countries",
    value: "85",
  },
  {
    label: "Visits/Month",
    value: "325K",
  },
  {
    label: "Social Media Followers",
    value: "125K+",
  },
];

export default function AboutSection() {
  const controls = useAnimation();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
  const [animationStage, setAnimationStage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // This ensures animations only start after component is fully mounted
  useEffect(() => {
    // Initial check for mobile
    setIsMobile(window.innerWidth < 768);

    // Add window resize listener
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isInView) {
      // Start the animation sequence
      controls.start("initial");

      // After initial display of logo and text, move to second stage
      const timer1 = setTimeout(() => {
        controls.start("moveUp");
        setAnimationStage(1);
      }, 1000);

      // After movement animation completes, show the summary
      const timer2 = setTimeout(() => {
        setAnimationStage(2);
      }, 1500); // Increased time to allow for complete movement

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [controls, isInView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    initial: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  const logoVariants = {
    hidden: { opacity: 0 },
    initial: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    moveUp: {
      y: "-10vh", // Move up by 30% of viewport height
      scale: 0.9,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  console.log(isMobile);

  const textVariants = {
    hidden: { opacity: 0 },
    initial: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    moveUp: {
      y: isMobile ? "-100px" : "-120px", // Move up by 25% of viewport height
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const statsContainerVariants = {
    hidden: { opacity: 0, y: isMobile ? -40 : 30 },
    visible: {
      opacity: 1,
      y: isMobile ? -70 : 40,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const countUpVariants = {
    hidden: { opacity: 0, y: 0 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.section
      id='about'
      ref={sectionRef}
      initial='hidden'
      animate={controls}
      variants={containerVariants}
      className='flex flex-col items-center justify-center relative px-4 py-8 lg:py-16 md:py-[77px] overflow-hidden'
      style={{
        backgroundImage: `url(${background.src})`,
        backgroundSize: "cover",
        backgroundPosition: "top",
      }}>
      <div className='w-full min-h-[350px] md:min-h-[446px] flex items-center justify-center flex-col gap-4 md:gap-8 max-w-[1024px] mx-auto relative'>
        <motion.div
          className='w-full'
          variants={logoVariants}
          animate={animationStage >= 1 ? "moveUp" : "initial"}>
          <Image
            src={logoSvg}
            alt='Klarda Media'
            width={1024}
            height={102}
            className='w-full h-auto max-h-[120px]'
            priority
          />
        </motion.div>

        <motion.h4
          className='text-sm md:text-base lg:text-2xl text-textColor text-center max-w-[800px]'
          variants={textVariants}
          animate={animationStage >= 1 ? "moveUp" : "initial"}>
          We help new crypto projects build their brand, establish trust, and
          grow their audience through strategic marketing and branding
          solutions. With our expertise in the Web3 space, we ensure your
          project stands out in a competitive market.
        </motion.h4>

        {/* summary - only shown after animation stage 2 */}
        {animationStage >= 2 && (
          <motion.div
            className='bottom-[5%] md:bottom-[15%] border-about-gradient w-full flex flex-row items-center justify-around py-6 md:py-[22px] rounded-[15px] gap-6 sm:gap-2 mt-auto'
            initial='hidden'
            animate='visible'
            variants={statsContainerVariants}
            style={{
              backgroundImage:
               isMobile ? "none" : "linear-gradient(to right, rgba(255,255,255,0.1) 0%, rgba(0,100,241,0.1) 43%, rgba(3,67,241,0.1) 62%, rgba(9,0,241,0.1) 100%)",
              position: "absolute",
              left: 0,
              right: 0,
            }}>
            {summary.map((item) => (
              <motion.div
                key={item.label}
                className='h-auto sm:h-[106px] w-fit flex flex-col items-center justify-center'
                variants={countUpVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}>
                <motion.h2
                  className='text-lg sm:text-4xl md:text-[30px] lg:text-[50px] font-bold text-accent leading-tight md:leading-[68px]'
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                  }}>
                  {item.value}
                </motion.h2>
                <motion.h4
                  className='text-xs md:text-lg lg:text-2xl font-medium text-accent leading-tight md:leading-8 text-nowrap'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.5,
                  }}>
                  {item.label}
                </motion.h4>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </motion.section>
  );
}
